import React, { useState } from 'react';
import Dialog from './Dialog';

interface LinkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (url: string, text: string) => void;
  selectedText?: string;
}

const LinkDialog: React.FC<LinkDialogProps> = ({ isOpen, onClose, onInsert, selectedText = '' }) => {
  const [url, setUrl] = useState('');
  const [text, setText] = useState(selectedText);
  const [isValidUrl, setIsValidUrl] = useState(false);

  const validateUrl = (url: string) => {
    if (!url) {
      setIsValidUrl(false);
      return;
    }
    
    try {
      // Add protocol if missing
      const urlWithProtocol = url.startsWith('http://') || url.startsWith('https://') 
        ? url 
        : `https://${url}`;
      new URL(urlWithProtocol);
      setIsValidUrl(true);
    } catch {
      setIsValidUrl(false);
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value;
    setUrl(newUrl);
    validateUrl(newUrl);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidUrl && text.trim()) {
      // Add protocol if missing
      const finalUrl = url.startsWith('http://') || url.startsWith('https://') 
        ? url 
        : `https://${url}`;
      onInsert(finalUrl, text.trim());
      onClose();
      // Reset form
      setUrl('');
      setText('');
      setIsValidUrl(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Insert Link">
      <form onSubmit={handleSubmit} className="mj-link-dialog-form">
        <div className="mj-form-group">
          <label htmlFor="link-text">Link Text: *</label>
          <input
            id="link-text"
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Text to display"
            className="mj-form-input"
            required
          />
        </div>
        
        <div className="mj-form-group">
          <label htmlFor="link-url">URL: *</label>
          <input
            id="link-url"
            type="text"
            value={url}
            onChange={handleUrlChange}
            placeholder="https://example.com or example.com"
            className={`mj-form-input ${url && !isValidUrl ? 'mj-form-input-error' : ''}`}
            required
          />
          {url && !isValidUrl && (
            <div className="mj-form-error">Please enter a valid URL</div>
          )}
        </div>

        <div className="mj-dialog-preview">
          <h4>Preview:</h4>
          <div className="mj-link-preview">
            {text && isValidUrl ? (
              <a 
                href={url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mj-preview-link"
              >
                {text}
              </a>
            ) : (
              <span className="mj-preview-placeholder">
                {text || 'Link text'} {url && isValidUrl ? `(${url})` : ''}
              </span>
            )}
          </div>
        </div>

        <div className="mj-dialog-actions">
          <button type="button" onClick={onClose} className="mj-btn mj-btn-secondary">
            Cancel
          </button>
          <button 
            type="submit" 
            className="mj-btn mj-btn-primary"
            disabled={!isValidUrl || !text.trim()}
          >
            Insert Link
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default LinkDialog; 